import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { measureGeometry } from "../../../shared/api";
import { isPolygon, getGeometryKey } from "./helpers";

/**
 * Server-side measurement of the drawn polygons. Re-measures the whole
 * FeatureCollection whenever any polygon is created / edited / deleted and
 * exposes per-polygon values from the last settled response so unchanged
 * polygons keep their numbers while only new/edited ones show as measuring.
 */
export function useGeometryMeasure(features) {
  const polygonFeatures = useMemo(
    () => (features?.features || []).filter(isPolygon),
    [features],
  );
  const hasPolygons = polygonFeatures.length > 0;

  // Geometry-derived key: identical shapes → identical key → cache hit, which
  // makes undo/redo snapshot restores resolve instantly without a refetch.
  const measureKey = useMemo(
    () =>
      JSON.stringify(
        polygonFeatures.map((f) => [f.id, getGeometryKey(f.geometry)]),
      ),
    [polygonFeatures],
  );

  const query = useQuery({
    queryKey: ["geometry-measure", measureKey],
    enabled: hasPolygons, // empty collection: nothing to measure
    staleTime: Infinity, // same geometry ⇒ same measurement
    gcTime: 30 * 60 * 1000, // keep undo/redo cache around for a while
    retry: (failureCount, error) =>
      error?.status !== 400 && error?.status !== 401 && failureCount < 1,
    queryFn: () =>
      measureGeometry({
        polygons: { type: "FeatureCollection", features: polygonFeatures },
      }),
  });

  // id → { geometryKey, area, perimeter } from the last settled measurement.
  // Rebuilt wholesale from each response (every request sends the full
  // collection), so deleted polygons are pruned automatically.
  const [measuredById, setMeasuredById] = useState(() => new Map());

  useEffect(() => {
    // Only pair a response with the request that produced it: while a new key
    // is fetching, `isFetching` is true and this effect is skipped, so stale
    // data never overwrites the map. `geometryKey` comes from the request-side
    // features so we don't rely on the server echoing coordinates
    // byte-identically.
    if (!query.isSuccess || query.isFetching || !query.data?.polygons) return;
    const responseById = new Map(
      (query.data.polygons.features || []).map((f) => [
        f.id,
        f.properties || {},
      ]),
    );
    const next = new Map();
    for (const feature of polygonFeatures) {
      const props = responseById.get(feature.id);
      if (!props) continue;
      next.set(feature.id, {
        geometryKey: getGeometryKey(feature.geometry),
        area: props.area_sqm,
        perimeter: props.perimeter_m,
      });
    }
    setMeasuredById(next);
  }, [query.isSuccess, query.isFetching, query.data, polygonFeatures]);

  // Back to a clean slate when the canvas is cleared (reset / tab switch /
  // "New assessment").
  useEffect(() => {
    if (!hasPolygons) setMeasuredById((m) => (m.size ? new Map() : m));
  }, [hasPolygons]);

  return {
    measuredById,
    // Response for the *current* geometry — undefined while it is in flight.
    measureData: hasPolygons ? query.data : null,
    totalArea: hasPolygons ? (query.data?.area_sqm ?? null) : 0,
    totalPerimeter: hasPolygons ? (query.data?.perimeter_m ?? null) : 0,
    isMeasuring: hasPolygons && query.isFetching,
    measureError: hasPolygons ? query.error || null : null,
  };
}
